import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Activity, Users, SlidersHorizontal, Gauge, Radio, GitBranch, MessageSquareWarning, Megaphone, ShieldCheck, ScrollText, Building2 } from "lucide-react";

const TABS = [
  { key: "health", label: "Health", icon: Activity },
  { key: "personas", label: "Persona Templates", icon: Users },
  { key: "scoring", label: "Persona Scoring", icon: SlidersHorizontal },
  { key: "intent", label: "Buying Intent", icon: Gauge },
  { key: "signals", label: "Signal Taxonomy", icon: Radio },
  { key: "dealers", label: "Dealer Status", icon: Building2 },
  { key: "feedback", label: "Feedback", icon: MessageSquareWarning },
  { key: "versions", label: "Versions", icon: GitBranch },
  { key: "audience", label: "Audience", icon: Megaphone },
  { key: "privacy", label: "Privacy", icon: ShieldCheck },
  { key: "audit", label: "Audit Log", icon: ScrollText },
];

const Card = ({ children, testid }) => (
  <div className="bg-white rounded-2xl border border-border p-6" data-testid={testid}>{children}</div>
);
const Empty = ({ text }) => <div className="text-center py-10 text-muted-foreground">{text}</div>;

export default function AdminIntelligence() {
  const [tab, setTab] = useState("health");
  return (
    <div className="space-y-6 fade-up" data-testid="admin-intelligence-page">
      <div>
        <h1 className="font-serif text-2xl font-bold text-navy">Marine Buyer Intelligence</h1>
        <p className="text-sm text-muted-foreground">System governance, configuration, and troubleshooting. Separate from the dealer app.</p>
      </div>
      <div className="flex flex-wrap gap-1.5 border-b border-border pb-2">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`intel-tab-${t.key}`}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${tab === t.key ? "bg-navy text-white" : "text-navy hover:bg-navy/5"}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>
      {tab === "health" && <HealthTab />}
      {tab === "personas" && <PersonasTab />}
      {tab === "scoring" && <RulesTab component="persona" />}
      {tab === "intent" && <RulesTab component="intent" />}
      {tab === "signals" && <SignalsTab />}
      {tab === "dealers" && <DealersTab />}
      {tab === "feedback" && <FeedbackTab />}
      {tab === "versions" && <VersionsTab />}
      {tab === "audience" && <AudienceTab />}
      {tab === "privacy" && <PrivacyTab />}
      {tab === "audit" && <AuditTab />}
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="bg-white rounded-xl border border-border p-4">
    <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">{label}</div>
    <div className="font-serif text-2xl font-bold text-navy mt-1">{value}</div>
  </div>
);

function HealthTab() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/intelligence/health").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return <Empty text="Loading..." />;
  const na = (v) => (v == null ? "No data available" : v);
  return (
    <div className="space-y-6" data-testid="intel-health">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        <Stat label="Dealerships Using" value={`${d.dealerships_using_intelligence}/${d.dealerships_total}`} />
        <Stat label="Total Signals" value={d.total_signals} />
        <Stat label="Signals 24h" value={d.signals_24h} />
        <Stat label="Signals 7d" value={d.signals_7d} />
        <Stat label="Scored Prospects" value={d.scored_prospects} />
        <Stat label="Active Personas" value={d.active_personas} />
        <Stat label="Persona Scores" value={d.persona_scores_generated} />
        <Stat label="Intent Scores" value={d.intent_scores_generated} />
        <Stat label="Recommendations" value={d.recommendations_generated} />
        <Stat label="Sales Attributed" value={d.sales_attributed} />
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <Card testid="intel-health-insufficient">
          <h3 className="font-semibold text-navy mb-3">Dealerships with insufficient data</h3>
          {d.dealerships_insufficient_data?.length ? d.dealerships_insufficient_data.map((x) => (
            <div key={x.id} className="flex justify-between py-1.5 text-sm border-b last:border-0">
              <span className="text-navy">{x.name}</span><span className="text-muted-foreground">{x.signals} signals</span>
            </div>
          )) : <Empty text="None" />}
        </Card>
        <Card testid="intel-health-norecent">
          <h3 className="font-semibold text-navy mb-3">No recent signals (14+ days)</h3>
          {d.dealerships_no_recent_signals?.length ? d.dealerships_no_recent_signals.map((x) => (
            <div key={x.id} className="flex justify-between py-1.5 text-sm border-b last:border-0">
              <span className="text-navy">{x.name}</span><span className="text-red-600">Stale</span>
            </div>
          )) : <Empty text="None" />}
        </Card>
      </div>
      <Card testid="intel-health-failures">
        <h3 className="font-semibold text-navy mb-3">System failures</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <div><span className="text-muted-foreground">Signal ingestions</span><div className="font-semibold text-navy">{na(d.failed_signal_ingestions)}</div></div>
          <div><span className="text-muted-foreground">Scoring jobs</span><div className="font-semibold text-navy">{na(d.failed_scoring_jobs)}</div></div>
          <div><span className="text-muted-foreground">Attribution events</span><div className="font-semibold text-navy">{na(d.failed_attribution_events)}</div></div>
          <div><span className="text-muted-foreground">Audience sync</span><div className="font-semibold text-navy">{na(d.audience_sync_errors)}</div></div>
        </div>
      </Card>
    </div>
  );
}

function PersonasTab() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/intelligence/persona-templates").then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const toggle = async (t) => {
    await api.post(`/admin/intelligence/persona-templates/${t.id}/toggle`).catch(() => toast.error("Failed"));
    toast.success(`${t.name} ${t.active ? "deactivated" : "activated"}`); load();
  };
  return (
    <Card testid="intel-personas">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-navy">Master persona template library</h3>
        <span className="text-xs text-muted-foreground">Editing a template never overwrites dealer-customized personas.</span>
      </div>
      <div className="divide-y">
        {rows.map((t) => (
          <div key={t.id} className="flex items-center gap-4 py-3" data-testid={`intel-template-${t.id}`}>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-navy">{t.name} <span className="text-xs text-muted-foreground">v{t.version}</span></div>
              <div className="text-xs text-muted-foreground truncate">{t.description}</div>
              <div className="flex flex-wrap gap-1 mt-1">
                {(t.typical_categories || []).slice(0, 4).map((c) => <Badge key={c} variant="secondary" className="text-[10px]">{c}</Badge>)}
              </div>
            </div>
            <div className="text-xs text-muted-foreground text-right shrink-0">
              <div>{t.dealerships_using} using</div>
              <div>{t.dealerships_customized} customized</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-xs text-muted-foreground">{t.active ? "Active" : "Inactive"}</span>
              <Switch checked={!!t.active} onCheckedChange={() => toggle(t)} data-testid={`intel-template-toggle-${t.id}`} />
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function RulesTab({ component }) {
  const [data, setData] = useState(null);
  const [rules, setRules] = useState([]);
  const [inactivity, setInactivity] = useState(null);
  const load = () => api.get(`/admin/intelligence/scoring-rules/${component}`).then(({ data }) => {
    setData(data); setRules((data.active?.rules || []).map((r) => ({ ...r }))); setInactivity(data.active?.inactivity || null);
  }).catch(() => {});
  useEffect(() => { load(); }, [component]);
  const setWeight = (i, v) => setRules((rs) => rs.map((r, idx) => idx === i ? { ...r, weight: Number(v) } : r));
  const save = async () => {
    const payload = { rules, notes: "Updated via admin console" };
    if (component === "intent") payload.inactivity = inactivity;
    await api.put(`/admin/intelligence/scoring-rules/${component}`, payload)
      .then(() => toast.success("Saved as new version")).catch(() => toast.error("Save failed"));
    load();
  };
  if (!data) return <Empty text="Loading..." />;
  return (
    <Card testid={`intel-rules-${component}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-navy">{component === "persona" ? "Persona scoring rules" : "Buying intent rules"} <span className="text-xs text-muted-foreground">active v{data.active?.version}</span></h3>
        <Button size="sm" className="bg-navy text-white" onClick={save} data-testid={`intel-rules-save-${component}`}>Save as new version</Button>
      </div>
      <p className="text-xs text-muted-foreground mb-3">Editing creates a new version. Historical scores keep the version they were calculated under.</p>
      <div className="divide-y">
        {rules.map((r, i) => (
          <div key={r.id || i} className="flex items-center gap-3 py-2" data-testid={`intel-rule-${component}-${i}`}>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-navy text-sm">{r.name || r.signal_type || r.dimension}</div>
              <div className="text-xs text-muted-foreground truncate">{r.description || (r.signal_type ? `Signal: ${r.signal_type}` : `Dimension: ${r.dimension}`)}</div>
            </div>
            <Input type="number" value={r.weight} onChange={(e) => setWeight(i, e.target.value)} className="w-24" data-testid={`intel-rule-weight-${component}-${i}`} />
          </div>
        ))}
      </div>
      {component === "intent" && inactivity && (
        <div className="mt-4 pt-4 border-t">
          <div className="text-sm font-semibold text-navy mb-2">Inactivity decay</div>
          <div className="flex flex-wrap gap-4 text-sm">
            {["threshold_days", "per_days", "max"].map((k) => (
              <label key={k} className="flex items-center gap-2">
                <span className="text-muted-foreground">{k.replace("_", " ")}</span>
                <Input type="number" value={inactivity[k]} onChange={(e) => setInactivity((s) => ({ ...s, [k]: Number(e.target.value) }))} className="w-20" data-testid={`intel-inactivity-${k}`} />
              </label>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

function SignalsTab() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/intelligence/signals").then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const upd = async (st, patch) => {
    await api.put(`/admin/intelligence/signals/${st}`, patch).then(() => toast.success("Updated")).catch(() => toast.error("Failed"));
    load();
  };
  return (
    <Card testid="intel-signals">
      <h3 className="font-semibold text-navy mb-4">Buyer signal taxonomy ({rows.length})</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-muted-foreground border-b">
            <th className="py-2">Signal</th><th>Category</th><th>Source</th><th>Persona</th><th>Intent</th><th>Attribution</th><th>Active</th>
          </tr></thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.signal_type} className="border-b last:border-0" data-testid={`intel-signal-${s.signal_type}`}>
                <td className="py-2 font-medium text-navy">{s.name}</td>
                <td><Badge variant="secondary" className="text-[10px]">{s.category}</Badge></td>
                <td className="text-muted-foreground text-xs">{s.source}</td>
                <td>{s.persona_relevance ? "Yes" : "No"}</td>
                <td>{s.intent_relevance ? "Yes" : "No"}</td>
                <td>{s.attribution_relevance ? "Yes" : "No"}</td>
                <td><Switch checked={!!s.active} onCheckedChange={(v) => upd(s.signal_type, { active: v })} data-testid={`intel-signal-toggle-${s.signal_type}`} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function DealersTab() {
  const [dealers, setDealers] = useState([]);
  const [sel, setSel] = useState(null);
  const [status, setStatus] = useState(null);
  useEffect(() => { api.get("/admin/dealers").then(({ data }) => setDealers(data.dealers || data || [])).catch(() => {}); }, []);
  const pick = async (id) => {
    setSel(id); setStatus(null);
    const { data } = await api.get(`/admin/intelligence/dealers/${id}/status`);
    setStatus(data);
  };
  const toggleFlag = async (key, val) => {
    const { data } = await api.put(`/admin/intelligence/flags/${sel}`, { flags: { [key]: val } });
    setStatus((s) => ({ ...s, flags: data.flags })); toast.success("Flag updated");
  };
  return (
    <div className="grid gap-4 lg:grid-cols-3" data-testid="intel-dealers">
      <Card testid="intel-dealers-list">
        <h3 className="font-semibold text-navy mb-3">Dealerships</h3>
        <div className="divide-y max-h-[520px] overflow-y-auto">
          {dealers.map((d) => (
            <button key={d.id} onClick={() => pick(d.id)} data-testid={`intel-dealer-${d.id}`}
              className={`w-full text-left py-2 px-2 rounded-lg text-sm ${sel === d.id ? "bg-navy/5 font-semibold text-navy" : "text-navy hover:bg-secondary"}`}>
              {d.name}
            </button>
          ))}
        </div>
      </Card>
      <div className="lg:col-span-2">
        {!sel ? <Card><Empty text="Select a dealership to view Buyer Intelligence status." /></Card> : !status ? <Card><Empty text="Loading..." /></Card> : (
          <Card testid="intel-dealer-status">
            <h3 className="font-semibold text-navy mb-3">{status.dealership?.name}</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm mb-5">
              <div><span className="text-muted-foreground">Signals</span><div className="font-semibold text-navy">{status.signals_received}</div></div>
              <div><span className="text-muted-foreground">Last signal</span><div className="font-semibold text-navy">{status.last_signal_received ? new Date(status.last_signal_received).toLocaleDateString() : "None"}</div></div>
              <div><span className="text-muted-foreground">Active personas</span><div className="font-semibold text-navy">{status.active_personas}</div></div>
              <div><span className="text-muted-foreground">Scored prospects</span><div className="font-semibold text-navy">{status.scored_prospects}</div></div>
              <div><span className="text-muted-foreground">Sales attributed</span><div className="font-semibold text-navy">{status.sales_attributed}</div></div>
              <div><span className="text-muted-foreground">Data confidence</span><div className="font-semibold text-navy capitalize">{status.data_confidence}</div></div>
              <div><span className="text-muted-foreground">Meta</span><div className="font-semibold text-navy">{status.meta_connection}</div></div>
              <div><span className="text-muted-foreground">Google</span><div className="font-semibold text-navy">{status.google_connection}</div></div>
              <div><span className="text-muted-foreground">Audience</span><div className="font-semibold text-navy">{status.audience_activation}</div></div>
            </div>
            <div className="pt-4 border-t">
              <div className="text-sm font-semibold text-navy mb-3">Feature flags</div>
              <div className="grid sm:grid-cols-2 gap-2">
                {Object.entries(status.flags || {}).map(([k, v]) => (
                  <label key={k} className="flex items-center justify-between gap-2 text-sm bg-secondary/50 rounded-lg px-3 py-2">
                    <span className="text-navy">{k.replace(/_/g, " ")}</span>
                    <Switch checked={!!v} onCheckedChange={(nv) => toggleFlag(k, nv)} data-testid={`intel-flag-${k}`} />
                  </label>
                ))}
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

function FeedbackTab() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/intelligence/feedback").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return <Empty text="Loading..." />;
  return (
    <Card testid="intel-feedback">
      <h3 className="font-semibold text-navy mb-4">Dealer feedback ({d.total})</h3>
      {d.total === 0 ? <Empty text="No feedback submitted yet." /> : (
        <>
          <div className="flex flex-wrap gap-2 mb-4">
            {Object.entries(d.counts).map(([k, v]) => <Badge key={k} variant="secondary">{k.replace(/_/g, " ")}: {v}</Badge>)}
          </div>
          <div className="divide-y">
            {d.items.map((f) => (
              <div key={f.id} className="py-2 text-sm flex justify-between">
                <span className="text-navy">{f.verdict?.replace(/_/g, " ")}</span>
                <span className="text-muted-foreground">{new Date(f.created_at).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </Card>
  );
}

function VersionsTab() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/intelligence/versions").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return <Empty text="Loading..." />;
  return (
    <div className="space-y-4" data-testid="intel-versions">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {Object.entries(d.active).map(([c, v]) => (
          <div key={c} className="bg-white rounded-xl border border-border p-4">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">{c.replace(/_/g, " ")}</div>
            <div className="font-serif text-2xl font-bold text-navy mt-1">v{v ?? "-"}</div>
          </div>
        ))}
      </div>
      <Card testid="intel-versions-history">
        <h3 className="font-semibold text-navy mb-3">Version history</h3>
        <div className="divide-y">
          {d.history.map((v) => (
            <div key={v.id} className="flex items-center justify-between py-2 text-sm">
              <span className="text-navy">{v.component.replace(/_/g, " ")} <span className="text-muted-foreground">v{v.version}</span></span>
              <span className="text-muted-foreground">{v.status} · {new Date(v.activated_at).toLocaleDateString()} · {v.notes}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function AudienceTab() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/intelligence/audiences").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return <Empty text="Loading..." />;
  return (
    <Card testid="intel-audience">
      <h3 className="font-semibold text-navy mb-3">Audience activation</h3>
      {d.items?.length ? d.items.map((a, i) => <div key={i} className="py-2 text-sm">{a.audience_name}</div>) : <Empty text={d.note} />}
    </Card>
  );
}

function PrivacyTab() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/intelligence/privacy").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return <Empty text="Loading..." />;
  return (
    <Card testid="intel-privacy">
      <h3 className="font-semibold text-navy mb-1">Privacy and data controls</h3>
      <p className="text-xs text-muted-foreground mb-4">{d.note}</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-muted-foreground border-b">
            <th className="py-2">Dealer</th><th>Consent</th><th>Sync eligibility</th><th>Data source</th><th>Platform auth</th><th>Blocked</th>
          </tr></thead>
          <tbody>
            {d.items.map((r) => (
              <tr key={r.dealership_id} className="border-b last:border-0">
                <td className="py-2 text-navy">{r.name}</td>
                <td className="text-muted-foreground">{r.advertising_data_consent}</td>
                <td className="text-muted-foreground">{r.audience_sync_eligibility}</td>
                <td className="text-muted-foreground">{r.data_source}</td>
                <td className="text-muted-foreground">{r.platform_authorization}</td>
                <td className="text-muted-foreground">{r.records_blocked_from_activation}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function AuditTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/intelligence/audit").then(({ data }) => setRows(data)).catch(() => {}); }, []);
  return (
    <Card testid="intel-audit">
      <h3 className="font-semibold text-navy mb-4">Intelligence audit log</h3>
      {rows.length === 0 ? <Empty text="No admin actions logged yet." /> : (
        <div className="divide-y">
          {rows.map((a) => (
            <div key={a.id} className="py-2 text-sm flex items-center justify-between gap-4" data-testid={`intel-audit-${a.id}`}>
              <div>
                <span className="font-medium text-navy">{a.action?.replace(/_/g, " ")}</span>
                <span className="text-muted-foreground"> · {a.object_type}{a.object_id ? ` (${a.object_id})` : ""}</span>
              </div>
              <div className="text-xs text-muted-foreground shrink-0">{a.admin_email} · {new Date(a.timestamp).toLocaleString()}</div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
